import { useState } from 'react';
import { useCourseRecurrence, useSetRecurrence, useDeleteRecurrence } from '@/api/course-builder';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { cn } from '@/lib/utils';
import type { CreateRecurrenceInput } from '@/types';

const UNIT_LABELS: Record<string, string> = {
  days: 'días',
  weeks: 'semanas',
  months: 'meses',
};

type Props = { courseId: number };

export function RecurrenceSection({ courseId }: Props) {
  const { data: config, isLoading } = useCourseRecurrence(courseId);
  const setMutation = useSetRecurrence();
  const deleteMutation = useDeleteRecurrence();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<CreateRecurrenceInput>({
    frequency_value: 6,
    frequency_unit: 'months',
    is_active: true,
  });

  const handleEdit = () => {
    if (config) {
      setForm({
        frequency_value: config.frequency_value,
        frequency_unit: config.frequency_unit,
        is_active: config.is_active,
      });
    }
    setEditing(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setMutation.mutate({ courseId, data: form }, { onSuccess: () => setEditing(false) });
  };

  const handleDelete = () => {
    deleteMutation.mutate(courseId);
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <h3 className="mb-4 text-sm font-semibold text-foreground">Recurrencia</h3>

      {!editing && !config && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">Sin recurrencia configurada</p>
          <button
            onClick={handleEdit}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Configurar
          </button>
        </div>
      )}

      {!editing && config && (
        <div className="space-y-3">
          <div className="flex items-center gap-4">
            <span className="text-sm text-foreground">
              Cada <span className="font-medium">{config.frequency_value}</span>{' '}
              {UNIT_LABELS[config.frequency_unit] ?? config.frequency_unit}
            </span>
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-xs font-medium',
                config.is_active
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
              )}
            >
              {config.is_active ? 'Activa' : 'Inactiva'}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleEdit}
              className="rounded-lg border border-input bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-accent"
            >
              Editar
            </button>
            <button
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="rounded-lg border border-destructive/30 bg-background px-3 py-1.5 text-sm text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-50"
            >
              {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
            </button>
          </div>
        </div>
      )}

      {editing && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Frecuencia
              </label>
              <input
                type="number"
                min={1}
                value={form.frequency_value}
                onChange={(e) => setForm({ ...form, frequency_value: Number(e.target.value) })}
                className="w-24 rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Unidad</label>
              <select
                value={form.frequency_unit}
                onChange={(e) =>
                  setForm({
                    ...form,
                    frequency_unit: e.target.value as CreateRecurrenceInput['frequency_unit'],
                  })
                }
                className="rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="days">Días</option>
                <option value="weeks">Semanas</option>
                <option value="months">Meses</option>
              </select>
            </div>
            <label className={cn('flex items-center gap-2 text-sm text-foreground')}>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                className="rounded border-input"
              />
              Activa
            </label>
          </div>

          {setMutation.error && (
            <p className="text-sm text-destructive">{setMutation.error.message}</p>
          )}

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={setMutation.isPending}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              {setMutation.isPending ? 'Guardando...' : 'Guardar'}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="rounded-lg border border-input bg-background px-4 py-2 text-sm text-foreground transition-colors hover:bg-accent"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
